'use client';

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { authApi, setAuthToken, getAuthToken } from './api';
import { persistAuthSession } from './persistAuthSession';
import { setTelemetryIdentity } from './telemetry/client-error';

interface User {
  candidateId: string;
  email: string;
  fullName?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, tenantSlug?: string, companyName?: string, jobId?: string, redirectUrl?: string) => Promise<{ success: boolean; error?: string }>;
  verifyToken: (token: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      // Try to get user info from localStorage
      const storedUser = localStorage.getItem('vhire_user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch {
          // Invalid stored user, clear it
          localStorage.removeItem('vhire_user');
          setAuthToken(null);
        }
      }
    }
    setIsLoading(false);
  }, []);

  // Tell client-error telemetry who is signed in: the opaque candidate id only
  // (never the email or name this context also holds). Deliberately does NOT
  // clear on `user === null` — this provider is also mounted on recruiter
  // pages, where a null candidate must not wipe the recruiter's identity;
  // logout() below is the explicit clear.
  const candidateId = user?.candidateId;
  useEffect(() => {
    if (candidateId) setTelemetryIdentity({ userId: candidateId, role: 'candidate' });
  }, [candidateId]);

  // Send magic link email
  const login = useCallback(async (
    email: string,
    tenantSlug?: string,
    companyName?: string,
    jobId?: string,
    redirectUrl?: string
  ) => {
    try {
      // Pass tenant context for email branding AND job/redirect for apply flow
      const response = await authApi.sendMagicLink(email, jobId, redirectUrl, tenantSlug, companyName);
      if (response.success) {
        return { success: true };
      }
      return { success: false, error: response.error || 'Failed to send magic link' };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }, []);

  // Verify magic link token
  const verifyToken = useCallback(async (token: string) => {
    try {
      const response = await authApi.verifyMagicLink(token);
      // API returns flat response: { success, candidateId, email, jwtToken, ... }
      // Handle both nested (response.data) and flat response formats
      const data = (response.data || response) as {
        candidateId?: string;
        sessionToken?: string;
        jwtToken?: string;
        email?: string;
        fullName?: string;
        name?: string;
      };

      if (response.success && data.candidateId) {
        const sessionToken = data.jwtToken || data.sessionToken;
        const fullName = data.fullName || data.name || '';

        if (!sessionToken) {
          return { success: false, error: 'No session token received' };
        }

        // ✅ Route through persistAuthSession so OAuth and magic-link write
        // identical vhire_user shapes (May 5, 2026). Helper handles setAuthToken
        // + JWT decode + fullName enrichment via /me/full when not provided
        // up front. Magic-link supplies fullName inline so the enrichment hop
        // is skipped.
        const persisted = persistAuthSession(sessionToken, { fullName });
        const newUser: User = {
          candidateId: persisted?.candidateId || data.candidateId,
          email: persisted?.email || data.email || '',
          fullName: persisted?.fullName || (fullName || undefined),
        };

        setUser(newUser);

        return { success: true };
      }
      return { success: false, error: response.error || 'Invalid or expired link' };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    setAuthToken(null);
    setUser(null);
    localStorage.removeItem('vhire_user');
    setTelemetryIdentity(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        verifyToken,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
